import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';

import { RunStatusBadge } from '../../components/task-import-runs/RunStatusBadge';
import { taskImportRunsApiClient } from '../../services/api/taskImportRunsService';

export const TaskImportRunDetail: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();

  const retrieve = taskImportRunsApiClient.useTaskImportRunsRetrieveQuery(
    { id: runId as string },
    { skip: !runId, pollingInterval: 3000 },
  );

  const detail = taskImportRunsApiClient.useTaskImportRunsDetailQuery(
    { id: runId as string },
    { skip: !runId },
  );

  const run = retrieve.data;
  const info = detail.data;

  useEffect(() => {
    if (!retrieve.data) return;
    const terminal = ['COMPLETED', 'FAILED', 'CANCELLED'];
    if (terminal.includes(retrieve.data.status)) {
      retrieve.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retrieve.data]);

  return (
    <div className="p-4">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex flex-col">
          <h1 className="text-lg font-semibold">External CMS Import</h1>
          {run && (
            <div className="mt-1 flex items-center gap-2 text-sm text-gray-600">
              <RunStatusBadge phase={run.phase} />
              <span>Firm: {run.firm?.name ?? String(run.firm_id)}</span>
              <span>CMS: {run.cms_type}</span>
              {run.file_name && (
                <a
                  className="text-blue-700 hover:underline"
                  href={run.file_url ?? '#'}
                  target="_blank"
                  rel="noreferrer"
                >
                  Download report
                </a>
              )}
              {(() => {
                const createdAt = (run as unknown as { created_at?: string })
                  .created_at;
                return createdAt ? (
                  <span className="text-gray-500">
                    {new Date(createdAt).toLocaleString()}
                  </span>
                ) : null;
              })()}
            </div>
          )}
        </div>
        <Link
          className="text-sm text-blue-700 hover:underline"
          to="/profile/external-cms"
        >
          Back to runs
        </Link>
      </div>

      <div className="mb-4 rounded border border-gray-200 p-3">
        <h2 className="mb-2 text-sm font-semibold">Matter Summary</h2>
        {!info ? (
          <div className="text-sm text-gray-600">Loading…</div>
        ) : (
          <>
            <div className="mb-2 text-xs text-gray-600">
              Total matched: {info.total_matched_tasks} • Total unmatched:{' '}
              {info.total_unmatched_tasks} • Linked matters:{' '}
              {info.linked_matter_count} • Unlinked matters:{' '}
              {info.unlinked_matter_count}
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-2 py-2">Matter</th>
                    <th className="px-2 py-2">Linked</th>
                    <th className="px-2 py-2">Matched</th>
                    <th className="px-2 py-2">Unmatched</th>
                    <th className="px-2 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {info.matter_links.map((m) => (
                    <tr key={String(m.id)}>
                      <td className="px-2 py-2">
                        {m.matter_facets.matter_name}
                      </td>
                      <td className="px-2 py-2">
                        {m.matter_id ? 'Yes' : 'No'}
                      </td>
                      <td className="px-2 py-2">{m.matched_task_count}</td>
                      <td className="px-2 py-2">{m.unmatched_task_count}</td>
                      <td className="px-2 py-2">
                        <Link
                          className="text-blue-700 hover:underline"
                          to={`/profile/external-cms/runs/${runId}/matters/${m.id}`}
                        >
                          View tasks
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
